export type JobDelivery = {
  id: string;
  job_id: string;
  delivery_type: string;
  title: string;
  description: string | null;
  file_url: string;
  thumbnail_url: string | null;
  expires_at: string | null;
  is_active: boolean;
};

const DELIVERY_TYPE_ICONS: Record<string, string> = {
  gallery:     "ri-gallery-line",
  video:       "ri-video-line",
  zip:         "ri-file-zip-line",
  photos:      "ri-image-2-line",
  documents:   "ri-file-text-line",
  other:       "ri-link-m",
};

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  gallery:     "Online Gallery",
  video:       "Video",
  zip:         "ZIP Download",
  photos:      "Photos",
  documents:   "Documents",
  other:       "Other",
};

interface Props {
  deliveries: JobDelivery[];
  brandColor?: string;
}

export default function DeliveriesSection({ deliveries, brandColor = "#C8A96E" }: Props) {
  return (
    <div>
      <h2 className="text-[#0f172a] font-bold text-base mb-4 flex items-center gap-2">
        <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
          <i className="ri-send-plane-line"></i>
        </span>
        Final Deliveries
      </h2>

      <div className="space-y-3">
        {deliveries.map((d) => {
          const isExpired = !!d.expires_at && new Date(d.expires_at) < new Date();
          const typeLabel = DELIVERY_TYPE_LABELS[d.delivery_type] ?? d.delivery_type;
          const typeIcon  = DELIVERY_TYPE_ICONS[d.delivery_type] ?? "ri-link-m";

          return (
            <div
              key={d.id}
              className={`bg-white rounded-xl border p-5 flex items-start gap-4 transition-all ${
                isExpired ? "border-red-200" : "border-slate-200"
              }`}
            >
              <div className="shrink-0">
                {d.thumbnail_url ? (
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-50">
                    <img
                      src={d.thumbnail_url}
                      alt={d.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400">
                    <i className={`${typeIcon} text-2xl`}></i>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <p className="text-[#0f172a] font-semibold text-sm truncate">{d.title}</p>
                    <span className="text-slate-400 text-xs">{typeLabel}</span>
                  </div>
                  {isExpired ? (
                    <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 whitespace-nowrap">
                      Expired
                    </span>
                  ) : (
                    <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 whitespace-nowrap">
                      Available
                    </span>
                  )}
                </div>

                {d.description && (
                  <p className="text-slate-500 text-xs mt-1 mb-2 line-clamp-2">{d.description}</p>
                )}

                {d.expires_at && (
                  <p className={`text-xs mb-2 ${isExpired ? "text-red-500/60" : "text-slate-400"}`}>
                    {isExpired ? "Expired" : "Expires"}{" "}
                    {new Date(d.expires_at).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                )}

                {!isExpired && d.file_url && (
                  <a
                    href={d.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-white px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap hover:opacity-90"
                    style={{ backgroundColor: brandColor }}
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-external-link-line"></i>
                    </span>
                    Open / Download
                  </a>
                )}
                {!isExpired && !d.file_url && (
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-white px-3 py-1.5 rounded-lg bg-slate-300 cursor-not-allowed whitespace-nowrap">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </span>
                    Coming soon
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}